import { PartialDeep } from './../interfaces';

/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 *
 * @param objects - Objects to merge
 * @returns New object with merged key/values
 */
export function mergeDeep<T extends object>(source: T | undefined, ...objects: (PartialDeep<T> | undefined)[]) {
  return [source, ...objects].reduce<T>((prev, obj) => {
    if (!obj) {
      return prev;
    }

    Object.keys(obj).forEach((key) => {
      if (!obj || obj[key] === undefined) {
        return;
      }

      const previousValue = prev[key];
      const objectValue = obj[key];

      if (Array.isArray(previousValue) && Array.isArray(objectValue)) {
        prev[key] = previousValue.concat(...objectValue);
      } else if (isObject(previousValue) && isObject(objectValue)) {
        prev[key] = mergeDeep(previousValue, objectValue);
      } else {
        prev[key] = objectValue;
      }
    });

    return prev;
  }, {} as T);
}

/**
 * Checks if `value` is a literal `{}` object. Does not include arrays, functions, regexes, and constructors.
 *
 * @param value The value to check.
 * @returns `true` if `value` is an object, else `false`.
 * @example
 * isObject({});         // true
 * isObject([1, 2, 3]);  // false
 * isObject(Function);   // false
 * isObject(null);       // false
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isObject(value: any): value is object & { length?: never } {
  return !!value && value.constructor === Object;
}
